/**
 * Project (or any scope-prefix) file browser — upload, folders, search, inline
 * FileViewer. Extracted so Docs "Manage files" can mount the same UI without
 * rebuilding the browser.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  listFiles,
  uploadFile,
  createFolder,
  type StoreObject,
  type StoreTags,
} from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { FileViewer } from "../../components/FileViewer";
import { SectionCard, Icon, EmptyState } from "../../mui";
import { fmtBytes } from "../../mui/format";

const c = brainTokens.color;

interface FileEntry {
  kind: "folder" | "file";
  name: string;
  key: string;
  size?: number;
}

export function ProjectFilesTabMui({
  prefix,
  tags,
  onOpenPlan,
  initialFileKey,
}: {
  prefix: string;
  tags: StoreTags;
  onOpenPlan?: () => void;
  initialFileKey?: string | null;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [path, setPath] = useState("");
  const [objects, setObjects] = useState<StoreObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [viewing, setViewing] = useState<{ key: string; name: string } | null>(() => {
    if (!initialFileKey) return null;
    const name = initialFileKey.split("/").pop() ?? initialFileKey;
    return { key: initialFileKey, name };
  });
  const [query, setQuery] = useState("");
  const [fileType, setFileType] = useState<"all" | "folders" | "docs">("all");
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setObjects(await listFiles(prefix));
    } catch {
      enqueueSnackbar("Failed to load files.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [prefix, enqueueSnackbar]);
  useEffect(() => {
    void load();
  }, [load]);

  const fullPrefix = prefix + path;
  const entries = useMemo<FileEntry[]>(() => {
    const folders = new Map<string, FileEntry>();
    const files: FileEntry[] = [];
    for (const o of objects) {
      if (!o.key.startsWith(fullPrefix)) continue;
      const rest = o.key.slice(fullPrefix.length);
      if (!rest || rest === ".keep") continue;
      const slash = rest.indexOf("/");
      if (slash === -1) files.push({ kind: "file", name: rest, key: o.key, size: o.size });
      else {
        const name = rest.slice(0, slash);
        if (!folders.has(name)) folders.set(name, { kind: "folder", name, key: `${fullPrefix}${name}/` });
      }
    }
    return [...[...folders.values()].sort((a, b) => a.name.localeCompare(b.name)), ...files.sort((a, b) => a.name.localeCompare(b.name))];
  }, [objects, fullPrefix]);

  const filteredEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (fileType === "folders" && e.kind !== "folder") return false;
      if (fileType === "docs" && e.kind !== "file") return false;
      if (q && !e.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [entries, query, fileType]);

  const upload = async (files: FileList | File[]) => {
    setBusy(true);
    try {
      for (const f of Array.from(files)) await uploadFile(`${fullPrefix}${f.name}`, f, tags);
      enqueueSnackbar(`Uploaded ${Array.from(files).length} file(s).`, { variant: "success" });
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Upload failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const newFolder = async () => {
    const name = window.prompt("New folder name")?.trim();
    if (!name) return;
    try {
      await createFolder(`${fullPrefix}${name}/`, tags);
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Create folder failed.", { variant: "error" });
    }
  };

  const crumbs = ["root", ...path.split("/").filter(Boolean)];

  if (viewing) {
    const isPlan = viewing.name.toLowerCase() === "plan.md";
    return (
      <SectionCard title="Files">
        {isPlan && onOpenPlan && (
          <Box sx={{ mb: 1.5 }}>
            <Button size="small" variant="outlined" onClick={onOpenPlan} startIcon={<Icon name="file-text" size={14} />}>
              Open in Docs
            </Button>
          </Box>
        )}
        <FileViewer
          fileKey={viewing.key}
          name={viewing.name}
          onBack={() => setViewing(null)}
          onNavigate={(absoluteKey) => setViewing({ key: absoluteKey, name: absoluteKey.split("/").pop() ?? absoluteKey })}
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Files"
      actions={
        <>
          <Button variant="outlined" onClick={() => void newFolder()} startIcon={<Icon name="folder-plus" size={15} stroke={1.8} />}>
            New folder
          </Button>
          <Button variant="contained" disabled={busy} onClick={() => fileInput.current?.click()} startIcon={<Icon name="upload" size={15} stroke={1.8} />}>
            Upload
          </Button>
          <input ref={fileInput} type="file" multiple hidden onChange={(e) => e.target.files && void upload(e.target.files)} />
        </>
      }
      headerMb={20}
    >
      {path && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "12px", fontSize: 12.5, color: c.muted2, flexWrap: "wrap" }}>
          {crumbs.map((seg, i) => (
            <Box component="span" key={i} sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              {i > 0 && <Box component="span" sx={{ color: c.placeholder }}>›</Box>}
              <Box
                component="span"
                onClick={() => setPath(crumbs.slice(1, i + 1).join("/") + (i > 0 ? "/" : ""))}
                sx={{ cursor: "pointer", fontFamily: brainTokens.font.mono, "&:hover": { color: c.tealInk } }}
              >
                {seg}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder={`Search ${entries.length} files…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, minWidth: 180 }}
        />
        {(["all", "folders", "docs"] as const).map((t) => (
          <Button
            key={t}
            size="small"
            variant={fileType === t ? "contained" : "outlined"}
            onClick={() => setFileType(t)}
          >
            {t === "all" ? "All" : t === "folders" ? "Folders" : "Docs"}
          </Button>
        ))}
      </Box>

      {loading ? (
        <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading files…</Box>
      ) : filteredEntries.length === 0 ? (
        <EmptyState icon="folder" title={query || fileType !== "all" ? "No files match your search." : "Empty folder"}>
          {query || fileType !== "all" ? undefined : "Drop a file below, or create a folder."}
        </EmptyState>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {filteredEntries.map((e) => (
            <Box
              key={e.key}
              onClick={() => (e.kind === "folder" ? setPath(e.key.slice(prefix.length)) : setViewing({ key: e.key, name: e.name }))}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "11px",
                p: "14px 16px",
                border: `1px solid ${c.borderLight}`,
                borderRadius: "8px",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": { background: c.panelBg3 },
              }}
            >
              <Box sx={{ color: e.kind === "folder" ? c.tealInk2 : c.muted2, display: "flex", flex: "none" }}>
                <Icon name={e.kind === "folder" ? "folder" : "book"} size={18} />
              </Box>
              <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name}</Box>
              {e.kind === "file" && e.size != null && (
                <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.faint }}>
                  {fmtBytes(e.size)}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Box
        onDragOver={(ev) => { ev.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(ev) => { ev.preventDefault(); setDragOver(false); if (ev.dataTransfer.files.length) void upload(ev.dataTransfer.files); }}
        sx={{
          mt: "14px",
          border: `1.5px dashed ${dragOver ? c.teal : c.dropBorder}`,
          borderRadius: "10px",
          p: "34px",
          textAlign: "center",
          fontSize: 13.5,
          color: dragOver ? c.tealInk : c.faint,
          background: dragOver ? c.tealTint2 : "transparent",
        }}
      >
        Drop files to upload into{" "}
        <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12 }}>
          {path || "root"}
        </Box>{" "}
        — auto-tagged.
      </Box>
    </SectionCard>
  );
}
